import { DatePipe, NgIf } from '@angular/common';
import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faChevronRight, faFileLines, faXmark } from '@fortawesome/pro-light-svg-icons';
import { NbCardModule, NbDatepickerModule, NbDialogRef, NbInputModule } from '@nebular/theme';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { first } from 'rxjs/operators';
import { APIService, KPI } from '../../../@core/api';
import { DocumentsService, SaveToastService } from '../../../@core/utils';
import { RightPanelDialogService } from '../../../@core/utils/right-panel-dialog.service';
import { LinkedEquipmentDialogComponent } from '../linked-equipment-dialog/linked-equipment-dialog.component';
import { toDateValue, toIsoDate } from '../../utils/date.utils';

@Component({
  selector: 'app-document-sheet',
  templateUrl: './document-sheet.component.html',
  styleUrls: ['./document-sheet.component.scss'],
  standalone: true,
  imports: [
    NbCardModule, NbInputModule, NbDatepickerModule,
    TranslateModule, DatePipe, NgIf, FontAwesomeModule,
    FormsModule, ReactiveFormsModule,
  ],
})
export class DocumentSheetComponent implements OnInit, OnDestroy {
  @Input() public doc: KPI.Document | null = null;
  @Input() public siteId: number | null = null;

  public faXmark = faXmark;
  public faChevronRight = faChevronRight;
  public faFileLines = faFileLines;

  public form: UntypedFormGroup | null = null;
  public corporateNames: string[] = [];
  public filteredCorporates: string[] = [];
  private savingStartTime: number = 0;

  private subscriptions: Subscription[] = [];
  private readonly toDateValue = toDateValue;
  private readonly toIsoDate = toIsoDate;

  constructor(
    private ref: NbDialogRef<DocumentSheetComponent>,
    private api: APIService,
    private documentsService: DocumentsService,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
    private sideFormLevelService: RightPanelDialogService,
    private saveToast: SaveToastService,
  ) {}

  public ngOnInit(): void {
    this.buildForm();
    this.subscriptions.push(
      this.api.get_corporates().subscribe(corporates => {
        this.corporateNames = corporates.map(c => c.name);
        this.cdr.detectChanges();
      })
    );
    this.subscriptions.push(
      (this.ref as any).onBackdropClick.subscribe(() => this.ref.close())
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  public get isMaintenanceContract(): boolean {
    return !!this.doc?.is_maintenance_contract || this.doc?.type === 'Contrat de maintenance';
  }

  public get sheetTitle(): string {
    return this.isMaintenanceContract ? 'documents.sheet.maintenance_title' : 'documents.sheet.title';
  }

  public get linkedTo(): string {
    return (
      this.doc?.asset_label ||
      this.doc?.investment_label ||
      this.doc?.initiative_label ||
      this.doc?.perimeter_label ||
      '-'
    );
  }

  private buildForm(): void {
    this.form = new UntypedFormGroup({
      title: new UntypedFormControl(this.doc?.title || ''),
      link: new UntypedFormControl(this.doc?.link || ''),
      company: new UntypedFormControl(this.doc?.company || ''),
      expiry_date: new UntypedFormControl(this.toDateValue(this.doc?.expiry_date)),
    });
  }

  public getFormattedUrl(url: string | null | undefined): string {
    if (!url) return '';
    if (url.length > 80) return url.substring(0, 80) + ' (..)';
    return url;
  }

  public onUrlFocus(event: FocusEvent): void {
    const input = event.target as HTMLInputElement;
    if (this.form && input.value.includes(' (..)')) {
      input.value = this.form.get('link')?.value || '';
    }
  }

  public onUrlBlur(event: FocusEvent): void {
    const input = event.target as HTMLInputElement;
    if (this.form) {
      const urlValue = input.value.replace(' (..)', '');
      this.form.get('link')?.setValue(urlValue, { emitEvent: false });
      if (urlValue.length > 80) {
        input.value = this.getFormattedUrl(urlValue);
      }
    }
    if (this.form?.dirty) {
      this.save();
    }
  }

  public openUrl(event?: Event): void {
    if (event) { event.preventDefault(); event.stopPropagation(); }
    const url = this.form?.get('link')?.value || this.doc?.link;
    if (!url) return;
    let urlToOpen = url.trim();
    if (urlToOpen && !urlToOpen.match(/^https?:\/\//i)) {
      urlToOpen = 'https://' + urlToOpen;
    }
    window.open(urlToOpen, '_blank');
  }

  public onDateChange(date: Date, fieldName: string): void {
    this.form?.get(fieldName)?.setValue(date ?? null);
    if (this.form?.dirty) {
      this.save();
    }
  }

  public onTitleBlur(): void {
    if (this.form?.dirty) {
      this.save();
    }
  }

  public onTitleKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      (event.target as HTMLElement).blur();
    }
  }

  public onCompanyInputChange(value: string): void {
    if (!value || value.trim() === '') {
      this.filteredCorporates = [];
    } else {
      this.filteredCorporates = this.corporateNames.filter(name =>
        name.toLowerCase().includes(value.toLowerCase())
      );
    }
  }

  public onCompanyInputFocus(value: string): void {
    if (!value || value.trim() === '') {
      this.filteredCorporates = [...this.corporateNames];
    } else {
      this.onCompanyInputChange(value);
    }
  }

  public selectCorporate(name: string): void {
    this.form?.get('company')?.setValue(name);
    this.filteredCorporates = [];
    this.form?.markAsDirty();
    this.save();
  }

  public onCompanyBlur(): void {
    setTimeout(() => {
      this.filteredCorporates = [];
      if (this.form?.dirty) {
        this.save();
      }
    }, 200);
  }

  public save(): void {
    if (!this.doc || !this.form) return;
    this.setSaving();
    const values = this.form.getRawValue();
    const patch: Partial<KPI.Document> = {
      title: values.title,
      link: values.link,
    };
    if (this.isMaintenanceContract) {
      patch.company = values.company || '';
      patch.expiry_date = this.toIsoDate(values.expiry_date);
    }
    this.api.documents_patch(this.doc.id, patch).subscribe({
      next: (updatedDoc) => {
        this.doc = { ...this.doc, ...updatedDoc };
        this.form.markAsPristine();
        this.documentsService.clearDocumentsCache();
        this.documentsService.documentsHaveChanged();
        this.setSaved();
      },
      error: () => {
        this.saveToast.close();
        this.cdr.detectChanges();
      },
    });
  }

  private setSaving(): void {
    this.saveToast.showSaving();
    this.savingStartTime = Date.now();
  }

  private setSaved(): void {
    const remaining = Math.max(0, 300 - (Date.now() - this.savingStartTime));
    setTimeout(() => {
      this.saveToast.showSaved();
    }, remaining);
  }

  public openLinkedEquipmentDialog(): void {
    this.sideFormLevelService.open(LinkedEquipmentDialogComponent, {
      autoFocus: false,
      closeOnBackdropClick: false,
      hasScroll: false,
      hasBackdrop: true,
      backdropClass: 'document-sheet-backdrop',
      dialogClass: 'doc-linked-equipment-overlay',
      context: {
        documentId: this.doc?.id ?? null,
        linkedEquipmentIds: this.doc?.linked_equipment || [],
        asset: null,
        reportId: null,
        siteId: this.siteId,
        title: this.translate.instant('documents.sheet.linked_equipments_maintenance_title'),
      } as any,
    }).onClose.pipe(first()).subscribe((updatedIds: number[] | undefined) => {
      if (Array.isArray(updatedIds) && this.doc) {
        this.doc = { ...this.doc, linked_equipment: updatedIds };
      }
    });
  }

  public close(): void {
    this.ref.close();
  }
}
